/**
 * English names for the emojis the picker offers, so they can be typed instead of hunted for:
 * ":wink" offers 😉, ":wink:" becomes it outright.
 *
 * The names follow the ones people already have in their fingers from Slack, Teams and GitHub --
 * ":thumbsup", ":+1", ":tada", ":100" -- rather than the official Unicode wording, which nobody
 * types ("grinning face with smiling eyes").  Where a name is genuinely ambiguous both spellings are
 * listed as aliases; the first one is what the suggestion list shows.
 *
 * Kept alongside the picker because they describe the same set of emojis: anything added to one
 * belongs in the other, or it becomes an emoji you can only get at one of the two ways.
 */
export interface IEmojiShortcode {
	emoji: string;
	/** First name is the label; the rest are aliases people may reach for instead. */
	names: string[];
}

/**
 * Emojis whose name matches what's been typed, the ones that start with it first: "car" should lead
 * with 🚗 rather than 📇 card_index, which merely contains it -- but the contains matches are worth
 * keeping, since half of remembering a name is remembering the second word of it.
 *
 * Shared by the ":" autocomplete and the picker's search box so that typing the same thing into
 * either of them turns up the same emojis in the same order.
 *
 * An empty query matches nothing: it's the caller's business whether that means "everything" (the
 * picker, which falls back to its categories) or "nothing yet" (the autocomplete, which stays shut).
 */
export function findEmojiShortcodes(query: string, limit: number = 0): IEmojiShortcode[] {
	const searchText = (query || "").toLowerCase().trim();

	if (!searchText)
		return [];

	const startsWith: IEmojiShortcode[] = [];
	const contains: IEmojiShortcode[] = [];

	for (const shortcode of EMOJI_SHORTCODES) {
		if (shortcode.names.some(x => x.indexOf(searchText) === 0)) startsWith.push(shortcode);
		else if (shortcode.names.some(x => x.indexOf(searchText) > 0)) contains.push(shortcode);

		// The starts-with matches can't be beaten by anything found further down the table, so once
		// there are enough of those to fill the caller's list there's nothing left to look for.
		if (limit > 0 && startsWith.length >= limit)
			break;
	}

	const matches = startsWith.concat(contains);

	return (limit > 0) ? matches.slice(0, limit) : matches;
}

export const EMOJI_SHORTCODES: IEmojiShortcode[] = [
	// ---------------- Smileys ----------------
	{ emoji: "😀", names: ["grinning"] },
	{ emoji: "😃", names: ["smiley"] },
	{ emoji: "😄", names: ["smile", "happy"] },
	{ emoji: "😁", names: ["grin", "beaming"] },
	{ emoji: "😆", names: ["laughing", "satisfied"] },
	{ emoji: "😅", names: ["sweat_smile"] },
	{ emoji: "🤣", names: ["rofl", "rolling_on_the_floor_laughing"] },
	{ emoji: "😂", names: ["joy", "tears_of_joy"] },
	{ emoji: "🙂", names: ["slight_smile", "slightly_smiling_face"] },
	{ emoji: "🙃", names: ["upside_down", "upside_down_face"] },
	{ emoji: "😉", names: ["wink", "winking_face"] },
	{ emoji: "😊", names: ["blush", "smiling_eyes"] },
	{ emoji: "😇", names: ["innocent", "halo"] },
	{ emoji: "😍", names: ["heart_eyes", "in_love"] },
	{ emoji: "😘", names: ["kissing_heart", "blow_kiss"] },
	{ emoji: "😗", names: ["kissing"] },
	{ emoji: "😋", names: ["yum", "tasty"] },
	{ emoji: "😜", names: ["stuck_out_tongue_winking_eye", "winking_tongue"] },
	{ emoji: "🤪", names: ["zany_face", "crazy"] },
	{ emoji: "🤨", names: ["raised_eyebrow", "skeptical"] },
	{ emoji: "🧐", names: ["monocle", "face_with_monocle"] },
	{ emoji: "🤓", names: ["nerd", "nerd_face"] },
	{ emoji: "😎", names: ["sunglasses", "cool"] },
	{ emoji: "🥳", names: ["partying_face", "party_face"] },
	{ emoji: "😏", names: ["smirk"] },
	{ emoji: "😒", names: ["unamused"] },
	{ emoji: "😞", names: ["disappointed"] },
	{ emoji: "😔", names: ["pensive"] },
	{ emoji: "😟", names: ["worried"] },
	{ emoji: "😕", names: ["confused"] },
	{ emoji: "🙁", names: ["slight_frown", "slightly_frowning_face"] },
	{ emoji: "😣", names: ["persevere"] },
	{ emoji: "😖", names: ["confounded"] },
	{ emoji: "😫", names: ["tired_face"] },
	{ emoji: "😩", names: ["weary"] },
	{ emoji: "🥺", names: ["pleading", "pleading_face"] },
	{ emoji: "😢", names: ["cry"] },
	{ emoji: "😭", names: ["sob", "bawling"] },
	{ emoji: "😤", names: ["triumph", "huff"] },
	{ emoji: "😠", names: ["angry"] },
	{ emoji: "😡", names: ["rage", "pout"] },
	{ emoji: "🤬", names: ["cursing", "swearing"] },
	{ emoji: "🤯", names: ["exploding_head", "mind_blown"] },
	{ emoji: "😳", names: ["flushed"] },
	{ emoji: "🥵", names: ["hot_face", "overheating"] },
	{ emoji: "🥶", names: ["cold_face", "freezing"] },
	{ emoji: "😱", names: ["scream"] },
	{ emoji: "😨", names: ["fearful"] },
	{ emoji: "😰", names: ["anxious", "cold_sweat"] },
	{ emoji: "😥", names: ["sad_but_relieved"] },
	{ emoji: "😓", names: ["sweat"] },
	{ emoji: "🤗", names: ["hugs", "hugging"] },
	{ emoji: "🤔", names: ["thinking"] },
	{ emoji: "🤭", names: ["hand_over_mouth", "oops"] },
	{ emoji: "🤫", names: ["shushing", "quiet"] },
	{ emoji: "😬", names: ["grimacing", "yikes"] },
	{ emoji: "🙄", names: ["roll_eyes", "eye_roll"] },
	{ emoji: "😯", names: ["hushed", "surprised"] },
	{ emoji: "😴", names: ["sleeping", "asleep"] },
	{ emoji: "🤮", names: ["vomiting", "puke"] },
	{ emoji: "🤧", names: ["sneezing"] },
	{ emoji: "😷", names: ["mask", "face_with_mask"] },
	{ emoji: "🤒", names: ["sick", "thermometer_face"] },
	{ emoji: "🤕", names: ["injured", "head_bandage"] },
	{ emoji: "🥴", names: ["woozy"] },
	{ emoji: "😵", names: ["dizzy_face", "knocked_out"] },
	{ emoji: "🤠", names: ["cowboy"] },
	{ emoji: "🤡", names: ["clown"] },
	{ emoji: "👻", names: ["ghost"] },
	{ emoji: "💀", names: ["skull", "dead"] },

	// ---------------- Gestures ----------------
	{ emoji: "👍", names: ["thumbsup", "+1", "like", "yes"] },
	{ emoji: "👎", names: ["thumbsdown", "-1", "dislike"] },
	{ emoji: "👌", names: ["ok_hand", "ok"] },
	{ emoji: "🤌", names: ["pinched_fingers"] },
	{ emoji: "✌️", names: ["victory", "peace"] },
	{ emoji: "🤞", names: ["crossed_fingers", "fingers_crossed"] },
	{ emoji: "🤟", names: ["love_you_gesture"] },
	{ emoji: "🤘", names: ["metal", "horns"] },
	{ emoji: "🤙", names: ["call_me", "shaka"] },
	{ emoji: "👈", names: ["point_left"] },
	{ emoji: "👉", names: ["point_right"] },
	{ emoji: "👆", names: ["point_up"] },
	{ emoji: "👇", names: ["point_down"] },
	{ emoji: "☝️", names: ["index_up"] },
	{ emoji: "✋", names: ["raised_hand", "stop"] },
	{ emoji: "🤚", names: ["raised_back_of_hand"] },
	{ emoji: "🖐️", names: ["hand_splayed"] },
	{ emoji: "🖖", names: ["vulcan", "spock"] },
	{ emoji: "👋", names: ["wave", "hello", "bye"] },
	{ emoji: "🤝", names: ["handshake", "deal"] },
	{ emoji: "🙏", names: ["pray", "thanks", "please"] },
	{ emoji: "✍️", names: ["writing_hand", "writing"] },
	{ emoji: "💪", names: ["muscle", "flex", "strong"] },
	{ emoji: "🦾", names: ["mechanical_arm"] },
	{ emoji: "👏", names: ["clap", "applause"] },
	{ emoji: "🙌", names: ["raised_hands", "praise"] },
	{ emoji: "👐", names: ["open_hands"] },
	{ emoji: "🤲", names: ["palms_up"] },
	{ emoji: "🫡", names: ["salute", "saluting_face"] },
	{ emoji: "🫰", names: ["finger_heart"] },
	{ emoji: "👊", names: ["punch", "fist_bump"] },
	{ emoji: "✊", names: ["raised_fist"] },
	{ emoji: "🤛", names: ["fist_left"] },
	{ emoji: "🤜", names: ["fist_right"] },
	{ emoji: "🖕", names: ["middle_finger"] },
	{ emoji: "💅", names: ["nail_care", "nails"] },
	{ emoji: "👀", names: ["eyes", "look"] },
	{ emoji: "🧠", names: ["brain"] },
	{ emoji: "🗣️", names: ["speaking_head", "speaking"] },
	{ emoji: "👤", names: ["person", "silhouette"] },

	// ---------------- Hearts ----------------
	{ emoji: "❤️", names: ["heart", "red_heart", "love"] },
	{ emoji: "🧡", names: ["orange_heart"] },
	{ emoji: "💛", names: ["yellow_heart"] },
	{ emoji: "💚", names: ["green_heart"] },
	{ emoji: "💙", names: ["blue_heart"] },
	{ emoji: "💜", names: ["purple_heart"] },
	{ emoji: "🖤", names: ["black_heart"] },
	{ emoji: "🤍", names: ["white_heart"] },
	{ emoji: "🤎", names: ["brown_heart"] },
	{ emoji: "💔", names: ["broken_heart"] },
	{ emoji: "❣️", names: ["heart_exclamation"] },
	{ emoji: "💕", names: ["two_hearts"] },
	{ emoji: "💞", names: ["revolving_hearts"] },
	{ emoji: "💓", names: ["heartbeat"] },
	{ emoji: "💗", names: ["growing_heart", "heartpulse"] },
	{ emoji: "💖", names: ["sparkling_heart"] },
	{ emoji: "💘", names: ["cupid", "heart_arrow"] },
	{ emoji: "💝", names: ["gift_heart"] },
	{ emoji: "💯", names: ["100", "hundred", "perfect"] },
	{ emoji: "💢", names: ["anger"] },
	{ emoji: "💥", names: ["boom", "collision"] },
	{ emoji: "💫", names: ["dizzy"] },
	{ emoji: "💦", names: ["sweat_drops", "splash"] },
	{ emoji: "💨", names: ["dash"] },
	{ emoji: "🔥", names: ["fire", "lit"] },
	{ emoji: "✨", names: ["sparkles"] },
	{ emoji: "⭐", names: ["star"] },
	{ emoji: "🌟", names: ["glowing_star", "star2"] },
	{ emoji: "⚡", names: ["zap", "lightning"] },
	{ emoji: "🎉", names: ["tada", "party_popper", "celebrate"] },

	// ---------------- Work ----------------
	{ emoji: "🏠", names: ["house", "home"] },
	{ emoji: "🏢", names: ["office", "building"] },
	{ emoji: "🏗️", names: ["building_construction", "job_site"] },
	{ emoji: "🚧", names: ["construction", "barrier"] },
	{ emoji: "🧰", names: ["toolbox"] },
	{ emoji: "🔨", names: ["hammer"] },
	{ emoji: "🪛", names: ["screwdriver"] },
	{ emoji: "🔧", names: ["wrench"] },
	{ emoji: "🪚", names: ["saw"] },
	{ emoji: "🪜", names: ["ladder"] },
	{ emoji: "🧯", names: ["fire_extinguisher"] },
	{ emoji: "🧹", names: ["broom"] },
	{ emoji: "🧽", names: ["sponge"] },
	{ emoji: "🪣", names: ["bucket"] },
	{ emoji: "🚿", names: ["shower"] },
	{ emoji: "💧", names: ["droplet", "water"] },
	{ emoji: "🌊", names: ["ocean", "water_wave"] },
	{ emoji: "🌧️", names: ["rain"] },
	{ emoji: "⛈️", names: ["thunderstorm", "storm"] },
	{ emoji: "🌪️", names: ["tornado"] },
	{ emoji: "❄️", names: ["snowflake", "snow"] },
	{ emoji: "🌡️", names: ["thermometer", "temperature"] },
	{ emoji: "🔌", names: ["plug", "electric_plug"] },
	{ emoji: "💡", names: ["bulb", "idea", "light"] },
	{ emoji: "🔋", names: ["battery"] },
	{ emoji: "📏", names: ["ruler"] },
	{ emoji: "📐", names: ["triangular_ruler"] },
	{ emoji: "🗑️", names: ["wastebasket", "trash"] },
	{ emoji: "♻️", names: ["recycle"] },
	{ emoji: "🚚", names: ["truck", "delivery"] },
	{ emoji: "🚐", names: ["van", "minibus"] },
	{ emoji: "🛻", names: ["pickup_truck"] },
	{ emoji: "🚗", names: ["car"] },
	{ emoji: "✈️", names: ["airplane", "plane"] },
	{ emoji: "⛽", names: ["fuel", "gas"] },
	{ emoji: "🧪", names: ["test_tube"] },
	{ emoji: "🦠", names: ["microbe", "germ"] },
	{ emoji: "☣️", names: ["biohazard"] },
	{ emoji: "⚠️", names: ["warning", "caution"] },
	{ emoji: "📋", names: ["clipboard"] },
	{ emoji: "📝", names: ["memo", "note", "pencil"] },
	{ emoji: "📄", names: ["page", "document"] },
	{ emoji: "📇", names: ["card_index"] },
	{ emoji: "📦", names: ["package", "box"] },
	{ emoji: "📸", names: ["camera", "photo"] },
	{ emoji: "🔑", names: ["key"] },
	{ emoji: "🗝️", names: ["old_key"] },
	{ emoji: "🛠️", names: ["tools"] },
	{ emoji: "⚙️", names: ["gear", "settings"] },

	// ---------------- Time & money ----------------
	{ emoji: "⏰", names: ["alarm_clock", "alarm"] },
	{ emoji: "⏱️", names: ["stopwatch"] },
	{ emoji: "⌚", names: ["watch"] },
	{ emoji: "📅", names: ["calendar", "date"] },
	{ emoji: "🗓️", names: ["schedule", "spiral_calendar"] },
	{ emoji: "⏳", names: ["hourglass", "waiting"] },
	{ emoji: "⌛", names: ["hourglass_done", "times_up"] },
	{ emoji: "🔔", names: ["bell"] },
	{ emoji: "🔕", names: ["no_bell", "muted"] },
	{ emoji: "📞", names: ["phone_call", "telephone_receiver"] },
	{ emoji: "📱", names: ["mobile_phone", "cell"] },
	{ emoji: "💻", names: ["laptop", "computer"] },
	{ emoji: "🖥️", names: ["desktop_computer"] },
	{ emoji: "🖨️", names: ["printer"] },
	{ emoji: "📧", names: ["email"] },
	{ emoji: "📨", names: ["incoming_envelope"] },
	{ emoji: "💬", names: ["speech_balloon", "comment"] },
	{ emoji: "💭", names: ["thought_balloon"] },
	{ emoji: "📊", names: ["bar_chart"] },
	{ emoji: "📈", names: ["chart_up", "chart_increasing"] },
	{ emoji: "📉", names: ["chart_down", "chart_decreasing"] },
	{ emoji: "💰", names: ["moneybag", "money"] },
	{ emoji: "💵", names: ["dollar", "cash"] },
	{ emoji: "💳", names: ["credit_card"] },
	{ emoji: "🧾", names: ["receipt", "invoice"] },
	{ emoji: "🏦", names: ["bank"] },
	{ emoji: "✅", names: ["check", "done", "white_check_mark"] },
	{ emoji: "❌", names: ["x", "cross", "no"] },
	{ emoji: "❗", names: ["exclamation"] },
	{ emoji: "❓", names: ["question"] },
	{ emoji: "🔴", names: ["red_circle"] },
	{ emoji: "🟠", names: ["orange_circle"] },
	{ emoji: "🟡", names: ["yellow_circle"] },
	{ emoji: "🟢", names: ["green_circle"] },
	{ emoji: "🔵", names: ["blue_circle"] },
	{ emoji: "⚪", names: ["white_circle"] },
	{ emoji: "⚫", names: ["black_circle"] },
	{ emoji: "🚩", names: ["flag", "triangular_flag"] },
	{ emoji: "📍", names: ["pin", "location", "round_pushpin"] },
	{ emoji: "🎯", names: ["dart", "target", "bullseye"] },

	// ---------------- Food & fun ----------------
	{ emoji: "☕", names: ["coffee"] },
	{ emoji: "🍵", names: ["tea"] },
	{ emoji: "🥤", names: ["soda", "cup_with_straw"] },
	{ emoji: "🍺", names: ["beer"] },
	{ emoji: "🍻", names: ["beers", "cheers"] },
	{ emoji: "🥂", names: ["clinking_glasses", "champagne"] },
	{ emoji: "🍕", names: ["pizza"] },
	{ emoji: "🍔", names: ["burger", "hamburger"] },
	{ emoji: "🌮", names: ["taco"] },
	{ emoji: "🌯", names: ["burrito"] },
	{ emoji: "🍟", names: ["fries"] },
	{ emoji: "🥪", names: ["sandwich"] },
	{ emoji: "🥗", names: ["salad"] },
	{ emoji: "🍩", names: ["donut", "doughnut"] },
	{ emoji: "🍪", names: ["cookie"] },
	{ emoji: "🎂", names: ["birthday", "birthday_cake"] },
	{ emoji: "🍰", names: ["cake", "shortcake"] },
	{ emoji: "🍫", names: ["chocolate"] },
	{ emoji: "🍎", names: ["apple"] },
	{ emoji: "🍌", names: ["banana"] },
	{ emoji: "🎁", names: ["gift", "present"] },
	{ emoji: "🎈", names: ["balloon"] },
	{ emoji: "🏆", names: ["trophy"] },
	{ emoji: "🥇", names: ["first_place", "gold_medal"] },
	{ emoji: "🎊", names: ["confetti", "confetti_ball"] },
	{ emoji: "🎵", names: ["music", "musical_note"] },
	{ emoji: "🎧", names: ["headphones"] },
	{ emoji: "🏈", names: ["football"] },
	{ emoji: "⚽", names: ["soccer"] },
	{ emoji: "🏀", names: ["basketball"] },
	{ emoji: "🐶", names: ["dog"] },
	{ emoji: "🐱", names: ["cat"] },
	{ emoji: "🌞", names: ["sunny", "sun"] },
	{ emoji: "🌙", names: ["moon", "crescent_moon"] },
	{ emoji: "🌈", names: ["rainbow"] },
	{ emoji: "🌴", names: ["palm_tree"] },
	{ emoji: "🏖️", names: ["beach"] },
	{ emoji: "🚀", names: ["rocket", "launch"] },
	{ emoji: "🎣", names: ["fishing"] },
	{ emoji: "🎸", names: ["guitar"] }
];
